import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Copy, RefreshCw, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Column, DataTable } from "@/components/ui/data-table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CapacityTile } from "@/components/common/CapacityTile";
import { useStorageRegions } from "@/hooks/useAdminStorage";
import { RegionStorageStatsDto } from "@/types/admin-storage";
import { formatBytes } from "@/utils/formatBytes";
import { copyToClipboard } from "@/utils/clipboard";
import { formatRelative } from "./utils/relativeTime";

const formatPercent = (
  value: number | null | undefined,
  fractionDigits: number = 1,
): string => {
  if (value === null || value === undefined) return "—";
  return `${(value * 100).toFixed(fractionDigits)}%`;
};

export const StorageListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    data: aggregate,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useStorageRegions();
  const [now, setNow] = useState<Date>(() => new Date());

  React.useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 5000);
    return () => window.clearInterval(interval);
  }, []);

  const handleCopyCode = async (
    event: React.MouseEvent<HTMLButtonElement>,
    code: string,
  ) => {
    event.stopPropagation();
    const ok = await copyToClipboard(code);
    if (ok) toast.success(t("storage.list.copyCodeSuccess"));
  };

  if (isLoading) {
    return (
      <div className="p-12 text-sm text-muted-foreground">
        {t("storage.list.loading")}
      </div>
    );
  }

  if (isError || !aggregate) {
    return (
      <div className="flex flex-col gap-3 p-12 max-w-md">
        <div className="flex items-center gap-2 text-destructive">
          <ShieldAlert className="size-5" />
          <span className="text-sm font-medium">
            {t("storage.list.loadError")}
          </span>
        </div>
        <Button
          type="button"
          variant="new_outline"
          size="sm"
          onClick={() => refetch()}
          className="w-fit"
        >
          {t("storage.list.retry")}
        </Button>
      </div>
    );
  }

  const usagePercent = aggregate.usagePercent;
  const usagePercentDisplay = `${(usagePercent * 100).toFixed(1)}%`;
  const errorCount = aggregate.errors.length;

  const columns: Column<RegionStorageStatsDto>[] = [
    {
      key: "region",
      header: t("storage.list.table.region"),
      render: (_, row) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={(event) => handleCopyCode(event, row.region)}
              className="inline-flex items-center gap-1.5 font-mono text-xs hover:underline"
              aria-label={t("storage.list.copyCode")}
            >
              <span>{row.region}</span>
              <Copy className="size-3 text-muted-foreground" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{t("storage.list.copyCode")}</TooltipContent>
        </Tooltip>
      ),
    },
    {
      key: "storageType",
      header: t("storage.list.table.type"),
      render: (_, row) => (
        <Badge variant="new_outline">
          {t(`regions.storageType.${row.storageType}`)}
        </Badge>
      ),
    },
    {
      key: "endpoint",
      header: t("storage.list.table.endpoint"),
      render: (_, row) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.endpoint ?? "—"}
        </span>
      ),
    },
    {
      key: "bucket",
      header: t("storage.list.table.bucket"),
      render: (_, row) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.bucket}
        </span>
      ),
    },
    {
      key: "reachable",
      header: t("storage.list.table.status"),
      render: (_, row) =>
        row.reachable ? (
          <Badge variant="status_done">
            {t("storage.list.status.reachable")}
          </Badge>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Badge variant="status_canceled">
                  {t("storage.list.status.unreachable")}
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent>{row.error ?? "—"}</TooltipContent>
          </Tooltip>
        ),
    },
    {
      key: "usedBytes",
      header: t("storage.list.table.used"),
      render: (_, row) => formatBytes(row.usedBytes),
    },
    {
      key: "freeBytes",
      header: t("storage.list.table.free"),
      render: (_, row) => formatBytes(row.freeBytes),
    },
    {
      key: "totalBytes",
      header: t("storage.list.table.total"),
      render: (_, row) => formatBytes(row.totalBytes),
    },
    {
      key: "usagePercent",
      header: t("storage.list.table.usage"),
      render: (_, row) => formatPercent(row.usagePercent),
    },
    {
      key: "objectCount",
      header: t("storage.list.table.objects"),
      render: (_, row) =>
        row.objectCount === null ? "—" : row.objectCount.toLocaleString(),
    },
    {
      key: "fetchedAt",
      header: t("storage.list.table.fetched"),
      render: (_, row) => (
        <span className="text-xs text-muted-foreground">
          {formatRelative(row.fetchedAt, now)}
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-10 p-12 max-w-7xl">
      <header className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex flex-col gap-2 max-w-3xl">
            <h1 className="text-2xl font-semibold text-foreground">
              {t("storage.list.title")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("storage.list.description")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {errorCount > 0 ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Badge
                      variant="status_canceled"
                      className="inline-flex items-center gap-1"
                    >
                      <AlertTriangle className="size-3" />
                      {t("storage.list.unreachableWarning", {
                        count: errorCount,
                      })}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <ul className="text-xs flex flex-col gap-1">
                    {aggregate.errors.map((err) => (
                      <li key={err.region}>
                        <span className="font-mono">{err.region}</span>:{" "}
                        {err.message}
                      </li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            ) : null}
            <Button
              type="button"
              variant="new_outline"
              size="sm"
              disabled={isFetching}
              onClick={() => refetch()}
            >
              <RefreshCw
                className={isFetching ? "size-3.5 animate-spin" : "size-3.5"}
              />
              {t("storage.list.refresh")}
            </Button>
          </div>
        </div>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <CapacityTile
          label={t("storage.list.aggregate.total")}
          value={formatBytes(aggregate.totalBytes)}
        />
        <CapacityTile
          label={t("storage.list.aggregate.used")}
          value={formatBytes(aggregate.usedBytes)}
          hint={usagePercentDisplay}
          bar={{ percent: usagePercent * 100 }}
        />
        <CapacityTile
          label={t("storage.list.aggregate.free")}
          value={formatBytes(aggregate.freeBytes)}
        />
        <CapacityTile
          label={t("storage.list.aggregate.usage")}
          value={usagePercentDisplay}
          bar={{ percent: usagePercent * 100 }}
        />
      </section>

      <section className="flex flex-col gap-4">
        <div className="overflow-x-auto rounded-md border border-base-border">
          <DataTable
            data={aggregate.regions}
            columns={columns}
            getRowKey={(row) => row.region}
            withPagination={false}
            withSorting={false}
            emptyMessage={t("storage.list.table.empty")}
            onRowClick={(row) =>
              navigate(`/storage/regions/${encodeURIComponent(row.region)}`)
            }
          />
        </div>
      </section>
    </div>
  );
};
